import { supabase, getProdSupabaseClient } from './client';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';

export interface Campaign {
  campaign_number: string;
  recall_title: string;
  total_recalls: number;
  skillLevel?: number | null;
  mobile_servicing_eligible?: boolean;
  has_advance_notice_recalls?: boolean; // True if any recall in campaign has advance notice
}

// View-based campaign structure (matches prod.tenant_campaigns_view)
interface ViewCampaign {
  campaign_id: string;
  tenant_id: string;
  campaign_number: string;
  campaign_title: string;
  oem: string | null;
  type: string;
  first_seen_at: string;
  last_seen_at: string;
  has_active_recalls: boolean;
  campaign_metadata: Record<string, unknown> | null;
  campaign_created_at: string;
  campaign_updated_at: string;
  skill_level: number | null;
  mobile_servicing_eligible: boolean;
  campaign_status: string | null;
  status_reason: string | null;
  notes: string | null;
  preference_id: string | null;
  user_id: string | null;
  preference_created_at: string | null;
  preference_updated_at: string | null;
  total_recalls: number; // Total recalls count from materialized view
}

// Helper function to get tenant ID for campaign queries
async function getTenantId(): Promise<string | null> {
  try {
    return await getTenantIdFromSession(supabase);
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'getting tenant ID for campaigns');
    }
    return null;
  }
}

// Helper: Deduplicate campaigns by campaign_number, prioritizing current user's preferences
// Priority: 1) Current user's preference, 2) NULL user_id (base), 3) Any other user's
function deduplicateCampaigns(data: ViewCampaign[], userId: string): ViewCampaign[] {
  const uniqueCampaigns = new Map<string, ViewCampaign>();
  data.forEach((campaign: ViewCampaign) => {
    const existing = uniqueCampaigns.get(campaign.campaign_number);
    if (!existing) {
      uniqueCampaigns.set(campaign.campaign_number, campaign);
    } else if (campaign.user_id === userId) {
      // Priority 1: Current user's preference (always prefer)
      uniqueCampaigns.set(campaign.campaign_number, campaign);
    } else if (existing.user_id !== userId) {
      // Priority 3: Prefer NULL user_id over other users' preferences
      if (campaign.user_id === null && existing.user_id !== null) {
        uniqueCampaigns.set(campaign.campaign_number, campaign);
      }
    }
  });
  return Array.from(uniqueCampaigns.values());
}

// Helper: Transform ViewCampaign to Campaign format
function transformToCampaign(viewCampaign: ViewCampaign): Campaign {
  return {
    campaign_number: viewCampaign.campaign_number,
    recall_title: viewCampaign.campaign_title || '',
    total_recalls: viewCampaign.total_recalls || 0,
    skillLevel: viewCampaign.skill_level,
    mobile_servicing_eligible: viewCampaign.mobile_servicing_eligible || false,
    has_advance_notice_recalls: viewCampaign.campaign_status === 'Advance Notice'
  };
}

export const campaignAPI = {
  // Get all campaigns from prod.get_tenant_campaigns RPC function
  // Returns ALL global campaigns with tenant-specific recall counts
  async getCampaigns(): Promise<Campaign[]> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        console.warn('[campaigns] No tenant ID available, returning empty results');
        return [];
      }

      const prodClient = getProdSupabaseClient();
      // Use RPC function for tenant-specific campaign data
      const { data, error } = await prodClient.rpc('get_tenant_campaigns', {
        p_tenant_id: tenantId
      });

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'getting campaigns from RPC');
        }
        throw error;
      }

      // Transform view data to Campaign format
      const campaigns: Campaign[] = (data || []).map((viewCampaign: ViewCampaign) => ({
        campaign_number: viewCampaign.campaign_number,
        recall_title: viewCampaign.campaign_title || '',
        total_recalls: viewCampaign.total_recalls || 0,
        skillLevel: viewCampaign.skill_level,
        mobile_servicing_eligible: viewCampaign.mobile_servicing_eligible || false,
        has_advance_notice_recalls: viewCampaign.campaign_status === 'Advance Notice'
      }));

      return campaigns;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting campaigns');
        throw translateSystemError(error, 'getting campaigns');
      }
      throw error;
    }
  },

  // Get campaigns with user preferences from prod.get_tenant_campaigns RPC function
  // Returns ALL global campaigns with tenant-specific recall counts and preferences
  async getCampaignsWithPreferences(userId: string): Promise<Campaign[]> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        return [];
      }

      const prodClient = getProdSupabaseClient();
      // Use RPC function for tenant-specific campaign data
      const { data, error } = await prodClient.rpc('get_tenant_campaigns', {
        p_tenant_id: tenantId
      });

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'getting campaigns with preferences');
        }
        throw error;
      }

      // RPC now returns one row per campaign with proper tenant-specific data
      // Deduplication still useful if there are multiple preference records
      const uniqueData = deduplicateCampaigns(data || [], userId);
      return uniqueData.map(transformToCampaign);
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting campaigns with preferences');
        throw translateSystemError(error, 'getting campaigns with preferences');
      }
      throw error;
    }
  },

  // Sync campaigns from prod.cars_with_safety_recalls_mv to prod.campaigns
  // Extracts unique campaigns from the MV and upserts them to the campaigns table
  async syncCampaigns(): Promise<{
    inserted_count: number;
    updated_count: number;
    total_campaigns: number;
  }> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      const prodClient = getProdSupabaseClient();

      // Call the RPC function to sync campaigns
      const { data, error } = await prodClient.rpc('sync_campaigns_from_mv');

      if (error) {
        if (isSystemError(error)) {
          throw translateSystemError(error, 'syncing campaigns from materialized view');
        }
        throw error;
      }

      // The RPC function returns a table with one row containing the counts
      if (data && data.length > 0) {
        return {
          inserted_count: data[0].inserted_count || 0,
          updated_count: data[0].updated_count || 0,
          total_campaigns: data[0].total_campaigns || 0
        };
      }

      return {
        inserted_count: 0,
        updated_count: 0,
        total_campaigns: 0
      };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'syncing campaigns');
        throw translateSystemError(error, 'syncing campaigns');
      }
      throw error;
    }
  }
};
